import styled, { css } from "styled-components";
import { getLocale } from "react-i18nify";

import device from "src/styles/device";
import font from "src/styles/font";

import {
  TextField,
  Grid,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { UseUpload } from "src/context/UploadProductContext";
import { useDialog } from "src/context/DialogContext";
import QuantityInput from "src/components/ui/QuantityInput";

const WrapperGrid = styled.div`
  width: 100%;
  /* margin-top: 24px; */
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  border-radius: 16px;
  background-color: var(--white-color);

  ::-webkit-scrollbar {
    width: 5px;
    height: 7px;
    display: none;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--grey-color);
    border-radius: 100px;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--grey-color);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  @media ${device.tabletL} {
    padding-bottom: 0;
    margin-bottom: 24px;
    /* border-radius: 16px; */
  }
`;

const TextDetail = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey" ? "var(--darkgrey-color)" : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;
  text-align: ${({ center }) => (center ? "center" : "left")};

  :last-child {
    margin-bottom: 0;
  }

  ${({ nowrap }) =>
    nowrap
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          white-space: pre-line;
          word-break: break-word;
        `}
`;

const ProductDataGrid = ({
  formData,
  onChange,
  // onAppendElement, // add default product
  onRemoveElement,
  isPreview = false,
}) => {
  const { openDialog, closeDialog } = useDialog();

  const { masterdata } = UseUpload();

  const color_data = masterdata.color_data;

  const handleOnRemove = (index) => {
    if (formData.product_choice_quotations.length < 2) {
      onRemoveElement(index);
      // onAppendElement();
      onChange("", "", "");
    } else {
      onRemoveElement(index);
    }
  };

  /** Header tabel definition for preview mode
   *
   * @params props: MUI Grid item props
   * */
  const previewTable = {
    header: [
      {
        label: "สินค้า",
        props: {
          xs: 6,
          align: "left",
        },
      },
      {
        label: "จำนวน",
        props: {
          xs: 2,
          md: 2,
        },
      },
      {
        label: "ราคาต่อหน่วย",
        props: {
          xs: 2,
          md: 2,
        },
      },
      {
        label: "ราคารวมทั้งหมด",
        props: {
          xs: 2,
          md: 2,
        },
      },
    ],
    body: [
      {
        // product detail column
        props: {
          xs: 6,
          sx: { display: "flex", gap: 2 },
        },
        component: ({ product, index }) => (
          <>
            <Box sx={{ width: "max-content" }}>
              <img
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  objectPosition: "center",
                  borderRadius: "16px",
                }}
                src={
                  product?.product_choice_quotation?.group_image
                    ?.product_display_image
                    ? product?.product_choice_quotation?.group_image?.product_display_image.replace(
                        ".com",
                        ".com/300x300",
                      )
                    : ""
                }
                alt={
                  product?.product_choice_quotation?.group_image
                    ?.product_display_image
                }
              />
            </Box>

            <Box sx={{ maxWidth: "100%" }}>
              <TextDetail nowrap>
                {product?.product_choice_quotation?.product?.item_name}
              </TextDetail>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <img
                  style={{
                    width: "16px",
                    height: "16px",
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: "50%",
                  }}
                  src={
                    color_data.find(
                      (detail) =>
                        detail?._id ===
                        product?.product_choice_quotation?.color._id,
                    )?.icon_img || ""
                  }
                  alt={
                    product?.product_choice_quotation?.group_image
                      ?.product_display_image
                  }
                />

                <TextDetail color="grey" sx={{ ml: 1, mb: 0 }}>
                  {getLocale() === "th"
                    ? product?.product_choice_quotation?.color?.color_th
                    : product?.product_choice_quotation?.color?.color_en}
                </TextDetail>
              </Box>

              <TextDetail color="grey">
                {product?.product_choice_quotation?.size?.quantity === "size"
                  ? `${product?.product_choice_quotation?.size?.width} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size?.unit_width
                            ?.unit_th
                        : product?.product_choice_quotation?.size?.unit_width
                            ?.unit_en
                    } x ${product?.product_choice_quotation?.size?.height} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size?.unit_height
                            ?.unit_th
                        : product?.product_choice_quotation?.size?.unit_height
                            ?.unit_en
                    } x ${product?.product_choice_quotation?.size?.thickness} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size
                            ?.unit_thickness?.unit_th
                        : product?.product_choice_quotation?.size
                            ?.unit_thickness?.unit_en
                    }`
                  : product?.product_choice_quotation?.size?.quantity ===
                      "capacity"
                    ? `${
                        product?.product_choice_quotation?.size?.capacity_size
                      } ${
                        getLocale() === "th"
                          ? product?.product_choice_quotation?.size
                              ?.unit_capacity?.unit_th
                          : product?.product_choice_quotation?.size
                              ?.unit_capacity?.unit_en
                      }`
                    : getLocale() === "th"
                      ? "สินค้าสั่งทำพิเศษ"
                      : "Made by order"}
              </TextDetail>
            </Box>
          </>
        ),
      },
      {
        // volume column
        props: {
          xs: 2,
        },
        component: ({ product, index }) => (
          <TextDetail nowrap center>
            {product?.volume}
          </TextDetail>
        ),
      },
      {
        // price column
        props: {
          xs: 2,
        },
        component: ({ product, index }) => (
          <TextDetail nowrap center>
            {product?.price}
          </TextDetail>
        ),
      },
      {
        // total column
        props: {
          xs: 2,
        },
        component: ({ product, index }) => (
          <TextDetail nowrap center>
            {product?.total}
          </TextDetail>
        ),
      },
    ],
  };

  /** Header tabel definition for edit mode */
  const editTable = {
    header: [
      {
        label: "สินค้า",
        props: {
          xs: 4.5,
          md: 5.5,
          align: "left",
        },
      },
      {
        label: "จำนวน",
        props: {
          xs: 3,
          md: 2,
        },
      },
      {
        label: "ราคาต่อหน่วย",
        props: {
          xs: 2,
          md: 2,
        },
      },
      {
        label: "ราคารวมทั้งหมด",
        props: {
          xs: 2,
          md: 2,
        },
      },
      {
        label: "",
        props: {
          xs: 0.5,
        },
      },
    ],
    body: [
      {
        // product detail column
        props: {
          xs: 4.5,
          md: 5.5,
          sx: { display: "flex", gap: 2 },
        },
        component: ({ product, index }) => (
          <>
            <Box sx={{ width: "max-content" }}>
              <img
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  objectPosition: "center",
                  borderRadius: "16px",
                }}
                src={
                  product?.product_choice_quotation?.group_image
                    ?.product_display_image
                    ? product?.product_choice_quotation?.group_image?.product_display_image.replace(
                        ".com",
                        ".com/300x300",
                      )
                    : ""
                }
                alt={
                  product?.product_choice_quotation?.group_image
                    ?.product_display_image
                }
              />
            </Box>

            <Box sx={{ maxWidth: "100%" }}>
              <TextDetail nowrap>
                {product?.product_choice_quotation?.product?.item_name}
              </TextDetail>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <img
                  style={{
                    width: "16px",
                    height: "16px",
                    objectFit: "cover",
                    objectPosition: "center",
                    borderRadius: "50%",
                  }}
                  src={
                    color_data.find(
                      (detail) =>
                        detail?._id ===
                        product?.product_choice_quotation?.color._id,
                    )?.icon_img || ""
                  }
                  alt={
                    product?.product_choice_quotation?.group_image
                      ?.product_display_image
                  }
                />

                <TextDetail color="grey" sx={{ ml: 1, mb: 0 }}>
                  {getLocale() === "th"
                    ? product?.product_choice_quotation?.color?.color_th
                    : product?.product_choice_quotation?.color?.color_en}
                </TextDetail>
              </Box>

              <TextDetail color="grey">
                {product?.product_choice_quotation?.size?.quantity === "size"
                  ? `${product?.product_choice_quotation?.size?.width} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size?.unit_width
                            ?.unit_th
                        : product?.product_choice_quotation?.size?.unit_width
                            ?.unit_en
                    } x ${product?.product_choice_quotation?.size?.height} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size?.unit_height
                            ?.unit_th
                        : product?.product_choice_quotation?.size?.unit_height
                            ?.unit_en
                    } x ${product?.product_choice_quotation?.size?.thickness} ${
                      getLocale() === "th"
                        ? product?.product_choice_quotation?.size
                            ?.unit_thickness?.unit_th
                        : product?.product_choice_quotation?.size
                            ?.unit_thickness?.unit_en
                    }`
                  : product?.product_choice_quotation?.size?.quantity ===
                      "capacity"
                    ? `${
                        product?.product_choice_quotation?.size?.capacity_size
                      } ${
                        getLocale() === "th"
                          ? product?.product_choice_quotation?.size
                              ?.unit_capacity?.unit_th
                          : product?.product_choice_quotation?.size
                              ?.unit_capacity?.unit_en
                      }`
                    : getLocale() === "th"
                      ? "สินค้าสั่งทำพิเศษ"
                      : "Made by order"}
              </TextDetail>
            </Box>
          </>
        ),
      },
      {
        // volume column
        props: {
          xs: 3,
          md: 2,
        },
        component: ({ product, index }) => (
          <QuantityInput
            onChange={(value) =>
              onChange(null, `product_choice_quotations.${index}.volume`, value)
            }
            value={product?.volume}
            style={{ paddingTop: "14px" }}
          />
        ),
      },
      {
        // price column
        props: {
          xs: 2,
        },
        component: ({ product, index }) => (
          <TextField
            variant="outlined"
            fullWidth
            sx={{
              pt: "9px",
              ".MuiInputAdornment-root": {
                position: "absolute",
                left: "14px",
              },
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">฿</InputAdornment>
              ),
            }}
            value={product?.price}
          />
        ),
      },
      {
        // total column
        props: {
          xs: 2,
        },
        component: ({ product, index }) => (
          <TextField
            variant="outlined"
            fullWidth
            sx={{
              pt: "9px",
              ".MuiInputAdornment-root": {
                position: "absolute",
                left: "14px",
              },
            }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">฿</InputAdornment>
              ),
            }}
            value={product?.total}
          />
        ),
      },
      {
        // delete column button
        props: {
          xs: 0.5,
          sx: { position: "relative" },
        },
        component: ({ product, index }) => (
          <IconButton
            aria-label="delete"
            disabled={
              formData?.product_choice_quotations?.length < 2 &&
              !product.product_choice_quotation
            }
            onClick={() => {
              if (!!product.product_choice_quotation) {
                openDialog({
                  type: "confirm",
                  title: `คุณต้องการลบสินค้าใช่หรือไม่`,
                  onConfirm: () => {
                    handleOnRemove(index);
                    closeDialog();
                  },
                  maxWidth: 400,
                });
              } else {
                onRemoveElement(index);
              }
            }}
            sx={{
              position: "absolute",
              top: "10px",
              right: {
                xs: "-70%",
                lg: "-40%",
              },
              // top: "50%",
              // transform: "translate(-40%, -65%);",
              ":disabled": {
                ".MuiSvgIcon-root": {
                  color: "var(--darkgrey-color)",
                },
              },
            }}
          >
            <DeleteOutlineIcon sx={{ color: "var(--primary-color)" }} />
          </IconButton>
        ),
      },
    ],
  };

  return (
    <>
      <WrapperGrid>
        <Grid
          container
          sx={{
            m: 0,
            width: "100%",
            minWidth: {
              xs: "700px",
              sm: "720px",
              md: "unset",
            },
          }}
        >
          {/* Header */}
          <Grid
            item
            xs={12}
            sx={{
              px: 3,
              py: 2,
              backgroundColor: "var(--darkwhite-color)",
              fontSize: font.size.sm,
            }}
          >
            <Grid
              container
              columnSpacing={{ xs: 2, lg: 3 }}
              sx={{
                textAlign: "center",
                alignItems: "center",
              }}
            >
              {/* <Grid item xs={4.5} md={5.5} sx={{ textAlign: "left" }}>
                สินค้า
              </Grid>

              <Grid item xs={3} md={2}>
                จำนวน
              </Grid>

              <Grid item xs={2} md={2}>
                ราคาต่อหน่วย
              </Grid>

              <Grid item xs={2} md={2}>
                ราคารวมทั้งหมด
              </Grid>

              <Grid item xs={0.5}></Grid> */}

              {isPreview
                ? previewTable.header.map((header, index) => (
                    <Grid
                      item
                      key={`preview-header-${index}`}
                      {...header.props}
                    >
                      {header.label}
                    </Grid>
                  ))
                : editTable.header.map((header, index) => (
                    <Grid item key={`edit-header-${index}`} {...header.props}>
                      {header.label}
                    </Grid>
                  ))}
            </Grid>
          </Grid>

          {formData?.product_choice_quotations?.length > 0 &&
            formData.product_choice_quotations?.map((product, index) => (
              <>
                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 3,
                    fontSize: font.size.sm,
                    "&:not(:last-child)": {
                      borderBottom: "1px solid var(--lightgrey-color)",
                    },
                  }}
                  key={`product-desktop-${index}`}
                >
                  <Grid
                    container
                    columnSpacing={{ xs: 2, lg: 3 }}
                    sx={{
                      fontSize: font.size.md,
                      ".MuiAutocomplete-root": {
                        margin: 0,
                      },
                      ".MuiTextField-root": {
                        borderRadius: "24px",
                      },
                      ".MuiOutlinedInput-root": {
                        borderRadius: "24px",
                      },
                      ".MuiOutlinedInput-input": {
                        py: "10px",
                        textAlign: "center",
                      },
                      ".MuiInputBase-inputMultiline": {
                        p: 0,
                        textAlign: "left",
                      },
                      ".MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input":
                        {
                          px: 0,
                        },
                    }}
                  >
                    {/* <Grid
                      item
                      xs={4.5}
                      md={5.5}
                      sx={{ display: "flex", gap: 2 }}
                    >
                      <Box sx={{ width: "max-content" }}>
                        <img
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                            objectPosition: "center",
                            borderRadius: "16px",
                          }}
                          src={
                            product?.product_choice_quotation?.group_image
                              ?.product_display_image
                              ? product?.product_choice_quotation?.group_image?.product_display_image.replace(
                                  ".com",
                                  ".com/300x300",
                                )
                              : ""
                          }
                          alt={
                            product?.product_choice_quotation?.group_image
                              ?.product_display_image
                          }
                        />
                      </Box>
                      <Box sx={{ maxWidth: "100%" }}>
                        <TextDetail nowrap>
                          {
                            product?.product_choice_quotation?.product
                              ?.item_name
                          }
                        </TextDetail>

                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <img
                            style={{
                              width: "16px",
                              height: "16px",
                              objectFit: "cover",
                              objectPosition: "center",
                              borderRadius: "50%",
                            }}
                            src={
                              color_data.find(
                                (detail) =>
                                  detail?._id ===
                                  product?.product_choice_quotation?.color._id,
                              )?.icon_img || ""
                            }
                            alt={
                              product?.product_choice_quotation?.group_image
                                ?.product_display_image
                            }
                          />

                          <TextDetail color="grey" sx={{ ml: 1, mb: 0 }}>
                            {getLocale() === "th"
                              ? product?.product_choice_quotation?.color
                                  ?.color_th
                              : product?.product_choice_quotation?.color
                                  ?.color_en}
                          </TextDetail>
                        </Box>

                        <TextDetail color="grey">
                          {product?.product_choice_quotation?.size?.quantity ===
                          "size"
                            ? `${
                                product?.product_choice_quotation?.size?.width
                              } ${
                                getLocale() === "th"
                                  ? product?.product_choice_quotation?.size
                                      ?.unit_width?.unit_th
                                  : product?.product_choice_quotation?.size
                                      ?.unit_width?.unit_en
                              } x ${
                                product?.product_choice_quotation?.size?.height
                              } ${
                                getLocale() === "th"
                                  ? product?.product_choice_quotation?.size
                                      ?.unit_height?.unit_th
                                  : product?.product_choice_quotation?.size
                                      ?.unit_height?.unit_en
                              } x ${
                                product?.product_choice_quotation?.size
                                  ?.thickness
                              } ${
                                getLocale() === "th"
                                  ? product?.product_choice_quotation?.size
                                      ?.unit_thickness?.unit_th
                                  : product?.product_choice_quotation?.size
                                      ?.unit_thickness?.unit_en
                              }`
                            : product?.product_choice_quotation?.size
                                  ?.quantity === "capacity"
                              ? `${
                                  product?.product_choice_quotation?.size
                                    ?.capacity_size
                                } ${
                                  getLocale() === "th"
                                    ? product?.product_choice_quotation?.size
                                        ?.unit_capacity?.unit_th
                                    : product?.product_choice_quotation?.size
                                        ?.unit_capacity?.unit_en
                                }`
                              : getLocale() === "th"
                                ? "สินค้าสั่งทำพิเศษ"
                                : "Made by order"}
                        </TextDetail>
                      </Box>
                    </Grid>

                    <Grid item xs={3} md={2}>
                      <QuantityInput
                        onChange={(value) =>
                          onChange(
                            null,
                            `product_choice_quotations.${index}.volume`,
                            value,
                          )
                        }
                        value={product?.volume}
                        style={{ paddingTop: "14px" }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        sx={{
                          pt: "9px",
                          ".MuiInputAdornment-root": {
                            position: "absolute",
                            left: "14px",
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">฿</InputAdornment>
                          ),
                        }}
                        value={product?.price}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        sx={{
                          pt: "9px",
                          ".MuiInputAdornment-root": {
                            position: "absolute",
                            left: "14px",
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">฿</InputAdornment>
                          ),
                        }}
                        value={product?.total}
                      />
                    </Grid>

                    <Grid item xs={0.5} sx={{ position: "relative" }}>
                      <IconButton
                        aria-label="delete"
                        disabled={
                          formData?.product_choice_quotations?.length < 2 &&
                          !product.product_choice_quotation
                        }
                        onClick={() => {
                          if (!!product.product_choice_quotation) {
                            openDialog({
                              type: "confirm",
                              title: `คุณต้องการลบสินค้าใช่หรือไม่`,
                              onConfirm: () => {
                                handleOnRemove(index);
                                closeDialog();
                              },
                              maxWidth: 400,
                            });
                          } else {
                            onRemoveElement(index);
                          }
                        }}
                        sx={{
                          position: "absolute",
                          top: "10px",
                          right: {
                            xs: "-70%",
                            lg: "-40%",
                          },
                          // top: "50%",
                          // transform: "translate(-40%, -65%);",
                          ":disabled": {
                            ".MuiSvgIcon-root": {
                              color: "var(--darkgrey-color)",
                            },
                          },
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{ color: "var(--primary-color)" }}
                        />
                      </IconButton>
                    </Grid> */}

                    {isPreview
                      ? previewTable.body.map((body, bodyIndex) => (
                          <Grid
                            item
                            key={`preview-body-${bodyIndex}-index-${index}`}
                            {...body.props}
                          >
                            {body.component
                              ? body.component({ product, index })
                              : null}
                          </Grid>
                        ))
                      : editTable.body.map((body, bodyIndex) => (
                          <Grid
                            item
                            key={`edit-body-${bodyIndex}-index-${index}`}
                            {...body.props}
                          >
                            {body.component
                              ? body.component({ product, index })
                              : null}
                          </Grid>
                        ))}
                  </Grid>
                </Grid>
              </>
            ))}
        </Grid>
      </WrapperGrid>
    </>
  );
};

export default ProductDataGrid;
